import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Copy, Check, Trash2, RotateCw, KeyRound, AlertTriangle } from 'lucide-react';
import { listApiKeys, createApiKey, deleteApiKey, rotateApiKey, API_KEY_SCOPES, type ApiKeyScope } from '@/api/api-keys';
import type { ApiKey, CreateApiKeyResponse } from '@/api/api-keys';
import { queryKeys } from '@/api/queryKeys';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FieldHint } from '@/components/shared/FieldHint';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

/** Format an ISO 8601 date string as "Apr 29, 2026" or "Never". */
function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Relative time like "2 days ago" for last-used display. */
function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
}

// ---------------------------------------------------------------------------
// Copy-to-clipboard button with transient checkmark feedback
// ---------------------------------------------------------------------------
function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleCopy}
      aria-label="Copy to clipboard"
    >
      {copied ? (
        <Check className="h-4 w-4 text-status-allow" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// "Create API Key" dialog — multi-step: form → reveal plaintext key
// ---------------------------------------------------------------------------
interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
}

function CreateApiKeyDialog({ open, onClose }: CreateDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: createApiKey,
    onSuccess: (data) => {
      setCreatedKey(data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.list });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    },
  });

  function handleClose() {
    setName('');
    setExpiresAt('');
    setCreatedKey(null);
    setError(null);
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || scopes.length === 0) return;
    setError(null);
    createMutation.mutate({
      name: name.trim(),
      scopes,
      ...(expiresAt ? { expires_at: new Date(expiresAt).toISOString() } : {}),
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        {createdKey === null ? (
          <>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                Give this key a descriptive name so you can identify it later.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="key-name">
                  Name <span className="text-destructive">*</span>
                </label>
                <Input
                  id="key-name"
                  placeholder="e.g. CI pipeline, local dev"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Scopes (required)</legend>
                <p className="text-xs text-muted-foreground">
                  A key gets exactly the scopes you tick. Retrieval is an explicit opt-in.
                </p>
                <FieldHint>
                  For running retrievals from the SDK or an MCP host, tick Retrieve. That is the
                  scope the quickstart snippet needs.
                </FieldHint>
                {API_KEY_SCOPES.map((sc) => (
                  <label key={sc.value} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={scopes.includes(sc.value)}
                      onChange={(e) =>
                        setScopes((prev) =>
                          e.target.checked ? [...prev, sc.value] : prev.filter((v) => v !== sc.value),
                        )
                      }
                    />
                    <span>
                      <span className="font-medium">{sc.label}</span>
                      <span className="block text-xs text-muted-foreground">{sc.hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="key-expiry">
                  Expiry date <span className="text-muted-foreground text-xs">(optional)</span>
                </label>
                <Input
                  id="key-expiry"
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {error && (
                <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                  {error}
                </p>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!name.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Key'}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>API Key Created</DialogTitle>
              <DialogDescription>
                Copy this key now. It will not be shown again.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-md border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-status-warning">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <p className="text-xs">
                  This is the only time the full key will be shown.
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Your new API key</label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={createdKey.key}
                    className="font-mono text-xs"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <CopyButton value={createdKey.key} />
                </div>
              </div>

              <div className="text-sm text-muted-foreground space-y-1">
                <p>
                  <span className="font-medium text-foreground">Name:</span>{' '}
                  {createdKey.name}
                </p>
                <p>
                  <span className="font-medium text-foreground">Prefix:</span>{' '}
                  <code className="font-mono">{createdKey.prefix}</code>
                </p>
                {createdKey.expires_at && (
                  <p>
                    <span className="font-medium text-foreground">Expires:</span>{' '}
                    {formatDate(createdKey.expires_at)}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// "Rotate key" confirmation dialog
// ---------------------------------------------------------------------------
interface RotateDialogProps {
  apiKey: ApiKey | null;
  onClose: () => void;
}

function RotateKeyDialog({ apiKey, onClose }: RotateDialogProps) {
  const queryClient = useQueryClient();
  const [rotatedKey, setRotatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rotateMutation = useMutation({
    mutationFn: (id: string) => rotateApiKey(id),
    onSuccess: (data) => {
      setRotatedKey(data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.list });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to rotate key');
    },
  });

  function handleClose() {
    setRotatedKey(null);
    setError(null);
    onClose();
  }

  if (!apiKey) return null;

  return (
    <Dialog open={!!apiKey} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        {rotatedKey === null ? (
          <>
            <DialogHeader>
              <DialogTitle>Rotate API Key</DialogTitle>
              <DialogDescription>
                The existing key <code className="font-mono">{apiKey.prefix}...</code> will be
                immediately revoked and a new key will be issued.
              </DialogDescription>
            </DialogHeader>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={rotateMutation.isPending}
                onClick={() => rotateMutation.mutate(apiKey.id)}
              >
                {rotateMutation.isPending ? 'Rotating...' : 'Rotate Key'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Key Rotated</DialogTitle>
              <DialogDescription>
                The old key has been revoked. Copy your new key now.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-md border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-status-warning">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <p className="text-xs">This is the only time the full key will be shown.</p>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">New API key</label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={rotatedKey.key}
                    className="font-mono text-xs"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <CopyButton value={rotatedKey.key} />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// "Delete key" confirmation dialog
// ---------------------------------------------------------------------------
interface DeleteDialogProps {
  apiKey: ApiKey | null;
  onClose: () => void;
}

function DeleteKeyDialog({ apiKey, onClose }: DeleteDialogProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteApiKey(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.list });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to delete key');
    },
  });

  if (!apiKey) return null;

  return (
    <Dialog open={!!apiKey} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Revoke API Key</DialogTitle>
          <DialogDescription>
            This will immediately revoke{' '}
            <span className="font-medium text-foreground">{apiKey.name}</span> (
            <code className="font-mono">{apiKey.prefix}...</code>). Any systems using this
            key will lose access. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate(apiKey.id)}
          >
            {deleteMutation.isPending ? 'Revoking...' : 'Revoke Key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export function ApiKeysPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [keyToRotate, setKeyToRotate] = useState<ApiKey | null>(null);
  const [keyToDelete, setKeyToDelete] = useState<ApiKey | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.apiKeys.list,
    queryFn: listApiKeys,
  });

  const keys: ApiKey[] = data?.data ?? [];

  return (
    <div className="space-y-6 max-w-4xl animate-fade-in">
      <PageHeader
        eyebrow="Account"
        title="API Keys"
        subtitle="Manage API keys used to authenticate programmatic access to the Gateco API."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create API Key
          </Button>
        }
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Active Keys</CardTitle>
          <CardDescription>
            A key can only do what its scopes allow: retrieval is an explicit opt-in, and key
            management always needs a signed-in session. Keep keys secret and rotate them regularly.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 px-6 pb-6">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="flex items-center gap-2 px-6 pb-6 text-destructive text-sm">
              <AlertTriangle className="h-4 w-4" />
              Failed to load API keys. Please refresh.
            </div>
          ) : keys.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
              <KeyRound className="h-8 w-8 opacity-40" />
              <p className="text-sm">No API keys yet.</p>
              <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create your first key
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="px-6 py-3 text-left font-medium text-muted-foreground">Name</th>
                    <th className="px-6 py-3 text-left font-medium text-muted-foreground">Prefix</th>
                    <th className="px-6 py-3 text-left font-medium text-muted-foreground">Scopes</th>
                    <th className="px-6 py-3 text-left font-medium text-muted-foreground">Last Used</th>
                    <th className="px-6 py-3 text-left font-medium text-muted-foreground">Expires</th>
                    <th className="px-6 py-3 text-left font-medium text-muted-foreground">Created</th>
                    <th className="px-6 py-3 text-right font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((key) => {
                    const isExpired =
                      key.expires_at !== null && new Date(key.expires_at) < new Date();
                    return (
                      <tr key={key.id} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-6 py-3 font-medium">{key.name}</td>
                        <td className="px-6 py-3">
                          <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                            {key.prefix}...
                          </code>
                        </td>
                        <td className="px-6 py-3 text-muted-foreground">
                          {(key.scopes ?? []).length ? (key.scopes ?? []).join(', ') : '—'}
                        </td>
                        <td className="px-6 py-3 text-muted-foreground">
                          {relativeTime(key.last_used_at)}
                        </td>
                        <td className="px-6 py-3">
                          {key.expires_at === null ? (
                            <Badge variant="outline" className="text-xs">No expiry</Badge>
                          ) : isExpired ? (
                            <Badge
                              variant="outline"
                              className="text-xs border-destructive/40 bg-destructive/10 text-destructive"
                            >
                              Expired
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">
                              {formatDate(key.expires_at)}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-muted-foreground">
                          {formatDate(key.created_at)}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setKeyToRotate(key)}
                              aria-label={`Rotate key ${key.name}`}
                              title="Rotate key"
                            >
                              <RotateCw className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setKeyToDelete(key)}
                              aria-label={`Revoke key ${key.name}`}
                              title="Revoke key"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="rounded-lg border border-status-warning/30 bg-status-warning/5 p-4 text-sm text-muted-foreground">
        <p className="font-medium text-foreground mb-1">Security tips</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Never commit API keys to source control.</li>
          <li>Use environment variables or a secrets manager.</li>
          <li>Rotate keys periodically and revoke any that may be compromised.</li>
          <li>Set expiry dates for keys used in short-lived environments.</li>
        </ul>
      </div>

      {/* Dialogs */}
      <CreateApiKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <RotateKeyDialog
        apiKey={keyToRotate}
        onClose={() => setKeyToRotate(null)}
      />
      <DeleteKeyDialog
        apiKey={keyToDelete}
        onClose={() => setKeyToDelete(null)}
      />
    </div>
  );
}
